import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/api/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ClassificationBadge, StatusBadge } from '@/components/StatusBadge';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { Sparkles, ShieldAlert, Send, AlertOctagon } from 'lucide-react';
import { toast } from 'sonner';
import TESTIDS from '@/constants/testIds';

export default function CommentsPage() {
    const { brandId, accountId, brands } = useScope();
    const [comments, setComments] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [filter, setFilter] = useState('all');
    const [active, setActive] = useState(null);
    const [replies, setReplies] = useState([]);
    const [classifying, setClassifying] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [replyDraft, setReplyDraft] = useState('');
    const [draftId, setDraftId] = useState(null);
    const [draftMeta, setDraftMeta] = useState(null);

    const reload = async () => {
        const params = {};
        if (brandId) params.brand_id = brandId;
        if (accountId) params.account_id = accountId;
        if (filter !== 'all') params.classification = filter;
        const r = await apiClient.get('/inbox/comments', { params });
        setComments(r.data);
        if (r.data.length && !activeId) {
            setActiveId(r.data[0].id);
        }
    };

    useEffect(() => {
        reload();
    }, [brandId, accountId, filter]);

    useEffect(() => {
        if (!activeId) {
            setActive(null);
            setReplies([]);
            return;
        }
        apiClient.get(`/inbox/comments/${activeId}`).then((r) => {
            setActive(r.data.comment);
            setReplies(r.data.replies);
        });
        setReplyDraft('');
        setDraftId(null);
        setDraftMeta(null);
    }, [activeId]);

    const classify = async () => {
        if (!active) return;
        setClassifying(true);
        try {
            const r = await apiClient.post(`/inbox/comments/${active.id}/classify`);
            toast.success(`Classified as ${r.data.category}`);
            // reload to pick up classification
            const fresh = await apiClient.get(`/inbox/comments/${active.id}`);
            setActive(fresh.data.comment);
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Classification failed');
        } finally {
            setClassifying(false);
        }
    };

    const draftReply = async () => {
        if (!active) return;
        setDrafting(true);
        try {
            const r = await apiClient.post(`/inbox/comments/${active.id}/draft_reply`);
            setReplyDraft(r.data.draft_text || '');
            setDraftId(r.data.id);
            setDraftMeta(r.data);
            toast.success(r.data.send_immediately ? 'Reply ready' : 'Draft created — review before send');
            // reload replies list
            const fresh = await apiClient.get(`/inbox/comments/${active.id}`);
            setReplies(fresh.data.replies);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Reply draft failed');
        } finally {
            setDrafting(false);
        }
    };

    const send = async () => {
        if (!draftId) {
            toast.error('Generate a reply draft first');
            return;
        }
        try {
            await apiClient.patch(`/inbox/replies/${draftId}`, { draft_text: replyDraft, status: 'sent' });
            toast.success('Reply marked as sent (placeholder)');
            await reload();
            const fresh = await apiClient.get(`/inbox/comments/${active.id}`);
            setActive(fresh.data.comment);
            setReplies(fresh.data.replies);
            setReplyDraft('');
            setDraftId(null);
            setDraftMeta(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to send');
        }
    };

    return (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_440px]">
            <Card className="border-border bg-card">
                <div className="flex items-center justify-between border-b border-border p-3">
                    <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{comments.length} comments</div>
                        <h2 className="text-base font-semibold">Comments inbox</h2>
                    </div>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-[180px]" data-testid={TESTIDS.inboxFilterClassification}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All classifications</SelectItem>
                            <SelectItem value="positive">Positive</SelectItem>
                            <SelectItem value="question">Question</SelectItem>
                            <SelectItem value="lead">Lead</SelectItem>
                            <SelectItem value="spam">Spam</SelectItem>
                            <SelectItem value="troll">Troll</SelectItem>
                            <SelectItem value="support">Support</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <ul className="divide-y divide-border">
                    {comments.length === 0 ? (
                        <li className="p-6 text-sm text-muted-foreground" data-testid="empty-state-inbox-clear">Inbox is clear in this scope.</li>
                    ) : null}
                    {comments.map((c) => {
                        const brand = brands.find((b) => b.id === c.brand_id);
                        const accent = brandColorVar(brand?.color_slot);
                        return (
                            <li
                                key={c.id}
                                data-testid={TESTIDS.commentRow}
                                onClick={() => setActiveId(c.id)}
                                className={`flex cursor-pointer items-start justify-between gap-3 px-4 py-3 transition-colors duration-150 hover:bg-[hsl(var(--surface-2))] ${
                                    activeId === c.id ? 'bg-[hsl(var(--surface-2))]' : ''
                                }`}
                                style={{ '--brand-accent-current': accent }}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(${accent})` }} />
                                        <span className="font-mono text-[12px]">@{c.author_handle}</span>
                                        <span className="text-[11px] text-muted-foreground">on @{c.account_handle || 'unknown'}</span>
                                    </div>
                                    <p className="mt-1 line-clamp-2 text-sm">{c.body}</p>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                    <ClassificationBadge category={c.classification?.category} />
                                    <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </Card>

            <Card className="border-border bg-card p-4">
                {!active ? (
                    <div className="text-sm text-muted-foreground">Select a comment to view detail and draft a reply.</div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-mono text-sm">@{active.author_handle}</div>
                                <div className="text-[11px] text-muted-foreground">on @{active.account_handle} • {new Date(active.created_at).toLocaleString()}</div>
                            </div>
                            <ClassificationBadge category={active.classification?.category} />
                        </div>
                        <Card className="border-border bg-[hsl(var(--surface-2))] p-3">
                            <p className="text-sm">{active.body}</p>
                        </Card>
                        {active.classification ? (
                            <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-3 text-xs">
                                <div className="font-mono uppercase tracking-wider text-muted-foreground">classification</div>
                                <div>confidence: {(active.classification.confidence ?? 0).toFixed(2)}</div>
                                <div>flags: {(active.classification.risk_flags || []).join(', ') || 'none'}</div>
                                <div>review required: {active.classification.requires_human_review ? 'yes' : 'no'}</div>
                                <div className="mt-1">{active.classification.rationale}</div>
                            </div>
                        ) : (
                            <div className="text-xs text-muted-foreground">Not yet classified.</div>
                        )}
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={classify} disabled={classifying} data-testid={TESTIDS.commentClassifyButton}>
                                <Sparkles className="mr-1 h-3 w-3" /> {classifying ? 'Classifying…' : 'Classify'}
                            </Button>
                            <Button size="sm" onClick={draftReply} disabled={drafting} data-testid={TESTIDS.commentDraftReplyButton}>
                                <Sparkles className="mr-1 h-3 w-3" /> {drafting ? 'Drafting…' : 'Draft AI reply'}
                            </Button>
                        </div>
                        <div>
                            <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Reply draft</div>
                            <Textarea rows={4} value={replyDraft} onChange={(e) => setReplyDraft(e.target.value)} data-testid={TESTIDS.commentReplyEdit} />
                            {draftMeta ? (
                                <div className="mt-2 rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-2 text-[11px]">
                                    {draftMeta.send_immediately === false ? (
                                        <div className="flex items-start gap-2 text-[hsl(var(--warning))]">
                                            <ShieldAlert className="mt-0.5 h-3.5 w-3.5" />
                                            <div>
                                                <div className="font-semibold">Awaiting review</div>
                                                <div>{draftMeta.reason_for_review || 'Sensitive context — review before sending.'}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-muted-foreground">Safe to send. {draftMeta.voice_check_notes}</div>
                                    )}
                                    {draftMeta.contains_banned_words ? (
                                        <div className="mt-1 inline-flex items-center gap-1 text-[hsl(var(--danger))]"><AlertOctagon className="h-3 w-3" /> Contains banned words — edit before send.</div>
                                    ) : null}
                                </div>
                            ) : null}
                            <div className="mt-2 flex justify-end gap-2">
                                <Button size="sm" onClick={send} disabled={!draftId} data-testid={TESTIDS.commentReplySend}>
                                    <Send className="mr-1 h-3 w-3" /> Mark as sent
                                </Button>
                            </div>
                        </div>
                        {replies.length ? (
                            <div>
                                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Reply history</div>
                                <ul className="space-y-1">
                                    {replies.map((r) => (
                                        <li key={r.id} className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2 text-xs">
                                            <div className="flex items-center justify-between">
                                                <StatusBadge status={r.status === 'sent' ? 'replied' : 'draft'} />
                                                <span className="font-mono text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                                            </div>
                                            <p className="mt-1">{r.draft_text}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                )}
            </Card>
        </div>
    );
}
